/*
 * SundayBuilder · Screen 4 · Dossier Folio
 * ---------------------------------------------------------------------
 * A single scrollable parchment folio with a quiet sticky right-rail
 * index. No tabs, no accordions.
 * =================================================================== */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronRight, ChevronLeft } from "lucide-react";
import HymnRow from "../../components/HymnRow";
import SlotAnthemPicker from "../../features/choral-intelligence/SlotAnthemPicker";
import PlannerFirstBoard from "./PlannerFirstBoard";
import RCLTrackToggle, { readingsFromEnvelope } from "./RCLTrackToggle";
import "../../features/choral-intelligence/choral-intelligence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RCL_TRADITIONS = new Set([
  "episcopal", "anglican", "lutheran", "methodist",
  "presbyterian", "reformed",
]);
function isRclTradition(t) {
  return RCL_TRADITIONS.has((t || "").toLowerCase().split(/[\s/]+/)[0]);
}

const SECTIONS = [
  { id: "overview",              label: "Overview" },
  { id: "services",              label: "Services" },
  { id: "service-order",         label: "Service Order" },
  { id: "repertoire",            label: "Repertoire" },
  { id: "formation",             label: "Formation" },
  { id: "bulletin",              label: "Bulletin" },
];

export default function DossierFolio({
  plan, jobId,
  onRefine, onContinueExport, onPlanUpdate, sundayLabel, sundayDate,
}) {
  const [active, setActive] = useState("overview");
  const folioRef = useRef(null);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [plan]);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Which sections have content?
  const blankMode = !!(plan.music_intelligence && plan.music_intelligence.blank_mode);
  const has = {
    overview:              !!(plan.overview || plan.season_overview || plan.annual_strategy || plan.music_strategy),
    services:              (plan.services || []).length > 0,
    "service-order":       blankMode,
    repertoire:            !blankMode && (plan.services || []).some((s) => (s.hymns || []).length > 0),
    formation:             (plan.formation_opportunities || []).length > 0,
    bulletin:              !!plan.bulletin_copy,
  };

  // ---- Plan-level RCL track override (persists in report.planner_state) ----
  const tradition = plan.tradition || plan.denomination || plan.parish_tradition
    || (plan.planner_state && plan.planner_state.tradition) || "";
  const rclApplies = isRclTradition(tradition);
  const properId = plan.proper_id
    || (plan.services && plan.services[0] && plan.services[0].proper_id)
    || null;
  const [rclTrack, setRclTrack] = useState(
    (plan.planner_state && plan.planner_state.rcl_track) || null
  );
  const [rclEnvelope, setRclEnvelope] = useState(null);

  const persistTrack = useCallback(async (nextTrack) => {
    if (!jobId) return;
    try {
      const currentState = plan.planner_state || {};
      await axios.patch(`${API}/parish/job/${jobId}/planner-state`, {
        tradition: currentState.tradition || tradition || "",
        rcl_track: nextTrack || null,
        slots: currentState.slots || [],
      });
    } catch (_e) {
      // Non-fatal; the state will re-attempt on the next planner interaction.
    }
  }, [jobId, plan.planner_state, tradition]);

  const handleTrackChange = useCallback((next, env) => {
    if (env) setRclEnvelope(env);
    if (next && next !== rclTrack) {
      setRclTrack(next);
      persistTrack(next);
    }
  }, [rclTrack, persistTrack]);

  // Effective readings (from the track envelope when available; falls back
  // to whatever the plan already carries). Never duplicates the display.
  const effectiveReadingsFor = useMemo(() => {
    const out = {};
    if (rclEnvelope) {
      const r = readingsFromEnvelope(rclEnvelope);
      out.readings = r.readings;
      out.psalm = r.psalm;
    }
    return out;
  }, [rclEnvelope]);

  return (
    <div
      className="sb-dossier-shell"
      data-testid="sb-dossier"
      style={{
        maxWidth: 1000,
        margin: "56px auto 120px",
        padding: "0 40px",
        display: "grid",
        gridTemplateColumns: "1fr 200px",
        gap: 48,
        alignItems: "start",
      }}
    >
      <div ref={folioRef} style={{ minWidth: 0 }}>
        {sundayLabel && (
          <p className="sb-today" style={{ margin: "0 0 12px" }}>{sundayLabel}</p>
        )}
        <h1 className="sb-h1" style={{ marginBottom: 6 }}>
          {plan.title || "Your Worship Dossier"}
        </h1>
        <p style={{
          fontFamily: "var(--li-font-body)",
          fontSize: "0.9375rem",
          color: "var(--li-text-muted)",
          margin: "0 0 32px",
        }}>
          {plan.scope || "Single Sunday"}
          {plan.liturgical_color ? ` · ${plan.liturgical_color}` : ""}
        </p>

        {has.overview && (
          <section id="overview" style={{ marginBottom: 56 }} data-testid="sb-section-overview">
            <h2 style={sectionH2}>Overview</h2>
            {plan.overview && <p style={folioP}>{plan.overview}</p>}
            {plan.season_overview && <p style={folioP}>{plan.season_overview}</p>}
            {plan.annual_strategy && <p style={folioP}>{plan.annual_strategy}</p>}
            {plan.music_strategy && <p style={folioP}>{plan.music_strategy}</p>}
          </section>
        )}

        {has.services && (
          <section id="services" style={{ marginBottom: 56 }} data-testid="sb-section-services">
            <h2 style={sectionH2}>Services</h2>
            {(plan.services || []).map((s, i) => {
              const readings = (effectiveReadingsFor.readings && effectiveReadingsFor.readings.length > 0)
                ? effectiveReadingsFor.readings
                : (s.readings || []);
              const psalm = effectiveReadingsFor.psalm || s.psalm || "";
              return (
              <div key={i} data-testid={`sb-service-${i}`} style={{ marginBottom: 32 }}>
                <h3 style={sectionH3}>{s.name}{s.occasion ? ` · ${s.occasion}` : ""}{s.liturgical_color ? ` · ${s.liturgical_color}` : ""}</h3>
                {readings.length > 0 && (
                  <p style={folioP} data-testid={`sb-service-readings-${i}`}>
                    <b>Readings:</b> {readings.join(" · ")}
                    {i === 0 && rclApplies && properId && (
                      <RCLTrackToggle
                        properId={properId}
                        value={rclTrack}
                        onChange={handleTrackChange}
                        isRcl={rclApplies}
                      />
                    )}
                  </p>
                )}
                {psalm && <p style={folioP}><b>Psalm:</b> {psalm}</p>}
                {s.anthem_or_choir && <p style={folioP}><b>Anthem / Choir:</b> {s.anthem_or_choir}</p>}
                {s.liturgical_notes && <p style={folioP}><b>Liturgical Notes:</b> {s.liturgical_notes}</p>}
                {sundayDate && !blankMode && (
                  <div
                    data-testid={`sb-choral-slot-pickers-${i}`}
                    style={{ marginTop: 14 }}
                  >
                    <SlotAnthemPicker
                      date={sundayDate}
                      liturgical_moment="offertory"
                      serviceName={s.name}
                      readings={s.readings || []}
                    />
                    <SlotAnthemPicker
                      date={sundayDate}
                      liturgical_moment="communion"
                      serviceName={s.name}
                      readings={s.readings || []}
                    />
                    <SlotAnthemPicker
                      date={sundayDate}
                      liturgical_moment="entrance"
                      serviceName={s.name}
                      readings={s.readings || []}
                    />
                  </div>
                )}
              </div>
              );
            })}
          </section>
        )}

        {has["service-order"] && (
          <section id="service-order" style={{ marginBottom: 56 }} data-testid="sb-section-service-order">
            <PlannerFirstBoard
              jobId={jobId}
              sundayDate={sundayDate}
              sundaySeason={plan.season || plan.liturgical_color || ""}
              properId={properId}
              rclTrack={rclTrack}
              initialState={plan.planner_state || null}
            />
          </section>
        )}

        {has.repertoire && (
          <section id="repertoire" style={{ marginBottom: 56 }} data-testid="sb-section-repertoire">
            <h2 style={sectionH2}>Repertoire</h2>
            {(plan.services || []).map((s, i) => (
              (s.hymns || []).length > 0 && (
                <div key={i} style={{ marginBottom: 24 }}>
                  <h3 style={sectionH3}>{s.name}</h3>
                  <ul style={{ listStyle: "none", padding: 0 }}>
                    {s.hymns.map((h, j) => (
                      <HymnRow
                        key={j}
                        jobId={jobId}
                        serviceIndex={i}
                        hymnIndex={j}
                        hymn={h}
                        onReplaced={(newHymn) => {
                          const next = { ...plan, services: [...(plan.services || [])] };
                          const svc = { ...next.services[i], hymns: [...(next.services[i].hymns || [])] };
                          svc.hymns[j] = newHymn;
                          next.services[i] = svc;
                          onPlanUpdate(next);
                        }}
                      />
                    ))}
                  </ul>
                </div>
              )
            ))}
          </section>
        )}

        {has.formation && (
          <section id="formation" style={{ marginBottom: 56 }} data-testid="sb-section-formation">
            <h2 style={sectionH2}>Formation</h2>
            <ul style={{ paddingLeft: 20, ...folioP }}>
              {(plan.formation_opportunities || []).map((c, i) => <li key={i} style={{ marginBottom: 6 }}>{c}</li>)}
            </ul>
          </section>
        )}

        {has.bulletin && (
          <section id="bulletin" style={{ marginBottom: 56 }} data-testid="sb-section-bulletin">
            <h2 style={sectionH2}>Bulletin</h2>
            <p style={folioP}>{plan.bulletin_copy}</p>
          </section>
        )}

        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 48,
          paddingTop: 32,
          borderTop: "1px solid var(--li-border)",
        }}>
          <button className="sb-btn ghost" onClick={onRefine} data-testid="sb-refine">
            <ChevronLeft size={18} strokeWidth={2.5} /> Refine
          </button>
          <button className="sb-btn primary" onClick={onContinueExport} data-testid="sb-continue-export">
            Continue to Export
            <ChevronRight size={18} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Sticky right-edge index */}
      <nav
        role="navigation"
        aria-label="On this page"
        data-testid="sb-index-rail"
        style={{
          position: "sticky",
          top: 96,
          alignSelf: "start",
        }}
        className="sb-index-rail"
      >
        <p style={{
          fontFamily: "var(--li-font-body)",
          fontSize: "0.8125rem",
          color: "var(--li-text-muted)",
          margin: "0 0 12px",
          fontWeight: 500,
          textTransform: "none",
        }}>On this page</p>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {SECTIONS.filter((s) => has[s.id]).map((s) => (
            <li key={s.id}>
              <button
                data-testid={`sb-index-${s.id}`}
                onClick={() => scrollTo(s.id)}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: "4px 0 4px 12px",
                  borderLeft: active === s.id ? "2px solid var(--li-cast-bronze)" : "2px solid transparent",
                  color: active === s.id ? "var(--li-text-primary)" : "var(--li-text-muted)",
                  fontFamily: "var(--li-font-body)",
                  fontSize: "0.9375rem",
                  fontWeight: active === s.id ? 600 : 500,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "color 0.15s ease, border-color 0.15s ease",
                  width: "100%",
                }}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <style>{`
        @media (max-width: 900px) {
          .sb-dossier-shell { grid-template-columns: 1fr !important; }
          .sb-index-rail { display: none; }
        }
      `}</style>
    </div>
  );
}

const sectionH2 = {
  fontFamily: "var(--li-font-display)",
  fontSize: "1.75rem",
  fontWeight: 600,
  color: "var(--li-text-primary)",
  margin: "0 0 16px",
  letterSpacing: "-0.005em",
};
const sectionH3 = {
  fontFamily: "var(--li-font-display)",
  fontSize: "1.25rem",
  fontWeight: 600,
  color: "var(--li-text-primary)",
  margin: "20px 0 8px",
};
const folioP = {
  fontFamily: "var(--li-font-body)",
  color: "var(--li-text-primary)",
  fontSize: "1.0625rem",
  lineHeight: 1.75,
  margin: "0 0 12px",
};
